import React from 'react';

import { Document, Text, View } from '@react-pdf/renderer';

import { fmtDate, fmtDateTimeLong, fmtQty } from '../format';
import { PO_TYPE_META, type PoSource } from '../status';
import type {
  GoodsReceiptDetail,
  GoodsReceiptLine,
  PurchaseOrderType,
} from '../types';
import {
  PdfA4,
  type PdfColumn,
  PdfFooter,
  PdfHeader,
  PdfKV,
  PdfKVRow,
  PdfSignatures,
  PdfTable,
  pdfStyles,
  type PrintMeta,
  T,
} from './pdfKit';

/**
 * Goods receipt (posted GR): PO, source (labelled by the PO type when known),
 * delivery note no., lines; signatures.
 */
const GoodsReceiptPdf: React.FC<{
  detail: GoodsReceiptDetail;
  meta: PrintMeta;
  /** From the GR's PO; null when the PO could not be loaded (plain "Supplier"). */
  poType?: PurchaseOrderType | null;
  source?: PoSource | null;
}> = ({ detail, meta, poType = null, source = null }) => {
  const r = detail.receipt;
  const src = source ?? { label: 'Supplier', value: r.supplierName, sub: null };
  const columns: PdfColumn<GoodsReceiptLine>[] = [
    { title: 'No', width: 20, fixed: true, render: (_l, i) => <T>{i + 1}</T> },
    {
      title: 'SKU / barang',
      width: 2.6,
      render: (l) => (
        <View>
          <T mono bold>
            {l.sku}
          </T>
          <T>{l.itemName}</T>
        </View>
      ),
    },
    {
      title: 'Qty diterima',
      width: 1.2,
      align: 'right',
      render: (l) => (
        <Text>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>{fmtQty(l.qty)}</Text>{' '}
          {l.uomName}
        </Text>
      ),
    },
    {
      title: 'Exp',
      width: 1.1,
      render: (l) => <T>{l.expiredDate ? fmtDate(l.expiredDate) : '—'}</T>,
    },
    {
      title: 'Storage tujuan',
      width: 2.2,
      render: (l) => <T>{l.storagePath || l.storageName}</T>,
    },
    {
      title: 'Batch number',
      width: 1.4,
      render: (l) => <T mono>{l.batchNumber ?? '—'}</T>,
    },
  ];

  return (
    <Document title={`Goods receipt ${r.code}`}>
      <PdfA4>
        <PdfHeader
          title="Goods receipt"
          code={r.code}
          meta={`Diterima ${fmtDateTimeLong(r.arrivedAt ?? r.postedAt ?? r.createdAt)} · ${r.hub?.name ?? `Hub #${r.hubID}`}`}
          qr={meta.qr}
        />
        <PdfKVRow>
          <PdfKV
            label={
              poType ? `PO · ${PO_TYPE_META[poType].label}` : 'Purchase order'
            }
            value={r.purchaseOrderCode}
            mono
          />
          <PdfKV
            label="Client"
            value={r.client?.name ?? `Client #${r.clientID}`}
          />
          <PdfKV label={src.label} value={src.value} />
          <PdfKV label="Surat jalan pengirim" value={r.deliveryNoteNo} mono />
        </PdfKVRow>
        <PdfTable columns={columns} rows={detail.lines} />
        {detail.closesBackorder ? (
          <Text style={pdfStyles.note}>
            Sisa PO ditutup (backorder dibatalkan) dengan penerimaan ini.
          </Text>
        ) : null}
        {detail.notes ? (
          <Text style={[pdfStyles.small, { marginTop: 8 }]}>
            <Text style={pdfStyles.label}>Catatan </Text>
            {detail.notes}
          </Text>
        ) : null}
        <PdfSignatures
          labels={[
            'Diterima oleh (gudang)',
            poType === 'TRANSFER'
              ? 'Pengantar / hub asal'
              : poType === 'RETURN'
                ? 'Pengantar / pengirim retur'
                : 'Pengantar / vendor',
          ]}
        />
        <PdfFooter
          doc={`Goods receipt ${r.code}`}
          printedAt={meta.printedAt}
          printedBy={meta.printedBy}
        />
      </PdfA4>
    </Document>
  );
};

export default GoodsReceiptPdf;
